import type { CustomerPace, PeriodsPage, Summary, TrackedItem } from "../api";
import { Time } from "../Time";
import { pct } from "../metrics/format";
import { dayLabel, money, signedMoney, signedPct } from "../revenue/format";
import { stoppedSince } from "../revenue/PacePanel";

interface Tile {
  label: string;
  value: string;
  change?: { text: string; tone: "pos" | "neg" | "dim" };
  note?: React.ReactNode;
}

/**
 * The account at a glance: one tile per question a CSM opens the page to
 * answer. A tile whose source has nothing to say is left out rather than
 * drawn empty -- a customer on no platform tenant has no connect rate, and
 * a blank tile would read as a zero.
 */
export default function KeyNumbers({
  pace,
  periods,
  tracked,
  untracked,
}: {
  pace: CustomerPace | null | undefined;
  periods: PeriodsPage | null | undefined;
  tracked: TrackedItem[] | undefined;
  /** Open suggestions on the latest report nobody has tracked yet. */
  untracked: number | undefined;
}) {
  const tiles: Tile[] = [];

  if (pace) tiles.push(revenueTile(pace));

  const week = periods ? thisWeek(periods) : null;
  if (week) {
    tiles.push({
      label: `Connect rate, ${week.label}`,
      value: pct(week.cur.connect_rate),
      change: ppChange(week.cur.connect_rate, week.prev?.connect_rate),
      note: `vs ${pct(week.prev?.connect_rate)} ${week.against}`,
    });
    tiles.push({
      label: `Attempts per lead, ${week.label}`,
      value: week.cur.attempts_per_lead?.toFixed(2) ?? "—",
      change: relChange(week.cur.attempts_per_lead, week.prev?.attempts_per_lead),
      note: `vs ${week.prev?.attempts_per_lead?.toFixed(2) ?? "—"} ${week.against}`,
    });
  }

  if (tracked) tiles.push(actionTile(tracked, untracked));

  if (pace && pace.campaigns.length > 0) tiles.push(silenceTile(pace));

  if (tiles.length === 0) return null;

  return (
    <section className="ov-kpis" aria-label="Key numbers">
      {tiles.map((t) => (
        <div className="card ov-kpi" key={t.label}>
          <span className="dim">{t.label}</span>
          <strong className="ov-kpi-value">{t.value}</strong>
          {t.change && <span className={`ov-kpi-change ${t.change.tone}`}>{t.change.text}</span>}
          {t.note && <span className="dim">{t.note}</span>}
        </div>
      ))}
    </section>
  );
}

function revenueTile(p: CustomerPace): Tile {
  const c = p.currency;
  const through = dayLabel(p.as_of);
  const tile: Tile = { label: `Revenue, 1–${through}`, value: money(p.mtd, c) };
  if (!p.too_early) {
    const n = Number(p.delta);
    tile.change = {
      text: `${signedMoney(p.delta, c)} (${signedPct(p.delta_pct)}) vs same days last month`,
      tone: n > 0 ? "pos" : n < 0 ? "neg" : "dim",
    };
  }
  const parts = [
    p.projected_total ? `Projected ${money(p.projected_total, c)}` : null,
    p.prior_month ? `last month ${money(p.prior_month, c)}` : null,
  ].filter(Boolean);
  if (parts.length) tile.note = parts.join(" · ");
  return tile;
}

/**
 * This week so far against the same elapsed days of last week -- never
 * against last week whole, which would make every Tuesday look like a
 * collapse. A week with no dialling yet falls back to last week in full.
 */
function thisWeek(p: PeriodsPage) {
  const [w0, w1, w2] = p.weeks;
  if (w0 && w0.summary.attempts > 0) {
    return {
      label: "this week",
      cur: w0.summary,
      prev: w0.like_for_like_summary as Summary | null,
      against: "same days last week",
    };
  }
  if (w1 && w1.summary.attempts > 0) {
    return { label: "last week", cur: w1.summary, prev: w2?.summary ?? null, against: "the week before" };
  }
  return null;
}

function ppChange(cur: number | null, prev: number | null | undefined): Tile["change"] {
  if (cur === null || prev === null || prev === undefined) return undefined;
  const d = (cur - prev) * 100;
  return { text: `${d < 0 ? "−" : "+"}${Math.abs(d).toFixed(1)} pp`, tone: tone(d) };
}

function relChange(cur: number | null, prev: number | null | undefined): Tile["change"] {
  if (cur === null || !prev) return undefined;
  const d = ((cur - prev) / prev) * 100;
  // Fewer attempts per lead is a thinner dialling ladder: worse, not better.
  return { text: `${d < 0 ? "−" : "+"}${Math.abs(d).toFixed(0)}%`, tone: tone(d) };
}

const tone = (d: number): "pos" | "neg" | "dim" => (Math.abs(d) < 0.05 ? "dim" : d > 0 ? "pos" : "neg");

function actionTile(items: TrackedItem[], untracked: number | undefined): Tile {
  const open = items.filter((i) => i.is_open);
  const withEta = open.filter((i) => i.eta).length;
  const oldest = open.reduce<string | null>(
    (min, i) => (min === null || i.created_at < min ? i.created_at : min),
    null,
  );
  const tile: Tile = {
    label: "Action items",
    value: `${open.length} tracked open`,
    note: oldest ? (
      <>
        Oldest open since <Time value={oldest} mode="day" />
      </>
    ) : undefined,
  };
  const parts = [
    open.length > 0 ? `${withEta} with an ETA` : null,
    untracked ? `${untracked} suggested, untracked` : null,
  ].filter(Boolean);
  if (parts.length) {
    tile.change = {
      text: parts.join(" · "),
      tone: open.length > withEta || (untracked ?? 0) > 0 ? "neg" : "dim",
    };
  }
  return tile;
}

/**
 * Campaigns the pace calls stopped: billed last month, nothing for several
 * days now. Its own threshold rather than "idle since yesterday", which
 * would flag every campaign that skips Sundays. Counted through `as_of`,
 * never a partial today -- that would make everything look stopped before
 * lunch.
 */
function silenceTile(p: CustomerPace): Tile {
  const stopped = p.campaigns
    .filter((c) => c.state === "stopped")
    .sort((a, b) => (b.idle_days ?? Infinity) - (a.idle_days ?? Infinity));
  if (stopped.length === 0) {
    return {
      label: "Stopped campaigns",
      value: "None",
      note: `All ${p.campaigns.length} billing through ${dayLabel(p.as_of)}`,
    };
  }
  const worst = stopped[0];
  return {
    label: "Stopped campaigns",
    value: `${stopped.length} of ${p.campaigns.length}`,
    change: { text: `${worst.name}: ${stoppedSince(worst)}`, tone: "neg" },
    note: `Billed through ${dayLabel(p.as_of)}`,
  };
}
